import type {LineItem, MapOptions, SalesforceInteractionsEvent} from './types'

interface GA4Item {
  item_id?: string | number
  price?: number | string
  quantity?: number | string
}

interface GA4Payload {
  event?: string
  ecommerce?: {
    currency?: string
    value?: number | string
    transaction_id?: string
    items?: GA4Item[]
  }
  logged_in?: string
  user_id?: string
  pageType?: string
  pageCategory?: string
  pagePath?: string
}

export const PARTY_IDENTIFICATION = {
  eventType: 'partyIdentification',
  IDType: 'CustomerNo',
  IDName: 'Iceland Customer Number'
}

export const CONTACT_POINT_EMAIL_EVENT_TYPE = 'contactPointEmail'

/**
 * The SDK's standard names, which it routes by name. Any other name is also tried as a custom
 * eventType: one with a hyphen logs an "Invalid eventType" warning, and a valid one adds an extra record.
 */
export const INTERACTION_NAMES = {
  addToCart: 'Add To Cart',
  removeFromCart: 'Remove From Cart',
  updateCart: 'Replace Cart',
  purchase: 'Purchase'
}

// Values Marketing Cloud's abandoned cart trigger filters on; swapped in just before sending
export const ENGAGEMENT_ACTIONS: Record<string, string> = {
  [INTERACTION_NAMES.addToCart]: 'cart-add',
  [INTERACTION_NAMES.removeFromCart]: 'cart-remove',
  [INTERACTION_NAMES.updateCart]: 'cart-update',
  [INTERACTION_NAMES.purchase]: 'order-accepted'
}

// Custom event type; must exist in the Data Cloud Web Connector schema before enabling
export const PAGE_VISIT_EVENT_TYPE = 'pageVisit'

interface PageVisitRule {
  pageKey: string
  pageName: string
  checkoutStep?: number
  includeBasketId?: boolean
  matches: (pageType: string, pagePath: string) => boolean
}

/**
 * Pages that send a pageVisit event. Adding a page here needs no schema change.
 * pageType alone is ambiguous for some pages: bonus card top-up also reports 'checkout',
 * and the confirmation page is reused for top-up and pre-book confirmations.
 * The basket is turned into the order on confirmation, so that page has no basketId.
 */
export const PAGE_VISITS: PageVisitRule[] = [
  {
    pageKey: 'basket',
    pageName: 'Basket',
    checkoutStep: 1,
    includeBasketId: true,
    matches: (pageType, pagePath) => pageType === 'cart' && pagePath.startsWith('/basket')
  },
  {
    pageKey: 'select_delivery',
    pageName: 'Select Delivery Type',
    checkoutStep: 2,
    includeBasketId: true,
    matches: (pageType) => pageType === 'Select Delivery Type'
  },
  {
    pageKey: 'book_a_slot',
    pageName: 'Book A Slot',
    checkoutStep: 3,
    includeBasketId: true,
    matches: (pageType) => pageType === 'Book A Slot'
  },
  {
    pageKey: 'before_you_go',
    pageName: 'Before You Go',
    checkoutStep: 4,
    includeBasketId: true,
    matches: (pageType) => pageType === 'beforeyougo'
  },
  {
    pageKey: 'checkout',
    pageName: 'Checkout',
    checkoutStep: 5,
    includeBasketId: true,
    matches: (pageType, pagePath) => pageType === 'checkout' && pagePath.startsWith('/checkout')
  },
  {
    pageKey: 'order_confirmation',
    pageName: 'Order Confirmation',
    checkoutStep: 6,
    matches: (pageType, pagePath) =>
      pageType === 'orderconfirmation' && pagePath.startsWith('/orderconfirmation')
  },
  {
    pageKey: 'favourites',
    pageName: 'Favourites',
    matches: (pageType) => pageType === 'wishlist'
  }
]

const DEFAULT_CURRENCY = 'GBP'

const isLoggedInUserData = (data: GA4Payload) =>
  data.event === 'userData' && data.logged_in === 'Logged In' && Boolean(data.user_id)

const toNumber = (value: unknown, fallback = 0) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

const validItems = (items?: GA4Item[]) =>
  (Array.isArray(items) ? items : []).filter((item) => item && item.item_id)

const toLineItem = (item: GA4Item, currency: string): LineItem => ({
  catalogObjectType: 'Product',
  catalogObjectId: String(item.item_id),
  quantity: toNumber(item.quantity, 1),
  price: toNumber(item.price),
  currency
})

const catalogEvent = (name: string, items: GA4Item[]): SalesforceInteractionsEvent | null =>
  items.length
    ? {interaction: {name, catalogObject: {type: 'Product', id: String(items[0].item_id)}}}
    : null

const singleLineItemEvent = (name: string, items: GA4Item[], currency: string) =>
  items.length ? {interaction: {name, lineItem: toLineItem(items[0], currency)}} : null

const mapToStandardEvent = (data: GA4Payload): SalesforceInteractionsEvent | null => {
  const ecommerce = data.ecommerce ?? {}
  const currency = ecommerce.currency || DEFAULT_CURRENCY
  const items = validItems(ecommerce.items)

  switch (data.event) {
    case 'view_item':
      return catalogEvent('View Catalog Object', items)
    case 'add_to_wishlist':
      return catalogEvent('Favorite Catalog Object', items)
    case 'add_to_cart':
      return singleLineItemEvent(INTERACTION_NAMES.addToCart, items, currency)
    case 'remove_from_cart':
      return singleLineItemEvent(INTERACTION_NAMES.removeFromCart, items, currency)
    case 'begin_checkout':
      return items.length
        ? {
            interaction: {
              name: INTERACTION_NAMES.updateCart,
              lineItems: items.map((item) => toLineItem(item, currency))
            }
          }
        : null
    case 'purchase':
      if (!ecommerce.transaction_id) return null
      return {
        interaction: {
          name: INTERACTION_NAMES.purchase,
          order: {
            id: String(ecommerce.transaction_id),
            totalValue: toNumber(ecommerce.value),
            currency,
            lineItems: items.map((item) => toLineItem(item, currency))
          }
        }
      }
    case 'userData':
      if (!isLoggedInUserData(data)) return null
      return {user: {attributes: {...PARTY_IDENTIFICATION, userId: String(data.user_id)}}}
    default:
      return null
  }
}

// The email comes from the customer store, as the GTM payload only has it hashed
const mapToContactPointEmailEvent = (
  data: GA4Payload,
  email?: string
): SalesforceInteractionsEvent | null =>
  isLoggedInUserData(data) && email
    ? {user: {attributes: {eventType: CONTACT_POINT_EMAIL_EVENT_TYPE, email}}}
    : null

const mapToPageVisitEvent = (
  data: GA4Payload,
  basketId?: string
): SalesforceInteractionsEvent | null => {
  if (data.event !== 'page_view' || !data.pageType) return null
  const rule = PAGE_VISITS.find(({matches}) => matches(data.pageType ?? '', data.pagePath ?? ''))
  if (!rule) return null

  return {
    interaction: {
      name: 'Page Visit',
      eventType: PAGE_VISIT_EVENT_TYPE,
      pageKey: rule.pageKey,
      pageName: rule.pageName,
      pageType: data.pageType,
      ...(data.pageCategory ? {pageCategory: String(data.pageCategory)} : {}),
      ...(rule.checkoutStep ? {checkoutStep: rule.checkoutStep} : {}),
      ...(rule.includeBasketId && basketId ? {basketId} : {})
    }
  }
}

/**
 * Maps a GA4 dataLayer payload to the Salesforce Interactions events to send.
 * Returns an empty list for events not sent to Data Cloud.
 */
export const mapToDataCloudEvents = (
  payload: unknown,
  options: MapOptions = {}
): SalesforceInteractionsEvent[] => {
  if (!payload || typeof payload !== 'object') return []
  const data = payload as GA4Payload
  const events = [mapToStandardEvent(data), mapToContactPointEmailEvent(data, options.email)]
  if (options.pageVisits) events.push(mapToPageVisitEvent(data, options.basketId))
  return events.filter((event): event is SalesforceInteractionsEvent => event !== null)
}
